import React from "react";
import { Link, useNavigate } from "react-router-dom";
import { Star, Play, Check, Clock, Heart, Mic } from "lucide-react";
import { useLang } from "../context/LanguageContext";
import { useAuth } from "../context/AuthContext";
import { usePlayer } from "../context/PlayerContext";
import { formatDuration } from "../mock";
import BookCover from "./BookCover";

export default function BookCard({ book, width = "w-[180px]" }) {
  const { t } = useLang();
  const navigate = useNavigate();
  const { hasBook, user, inWishlist, toggleWishlist } = useAuth();
  const { playTrack } = usePlayer();
  const owned = user && hasBook(book.id);
  const wished = user && inWishlist && inWishlist(book.id);
  const title = book.title_en;
  const to = `/book/${book.slug || book.id}`;

  const handlePlay = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (owned) playTrack(book, book.chapters?.[0], false);
    else playTrack(book, null, true);
  };

  const handleWish = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    const r = await toggleWishlist(book.id);
    if (r?.needsAuth) navigate("/login");
  };

  return (
    // The cover and the title are real links rather than a div with onClick:
    // that is what makes the card reachable by keyboard, openable in a new tab,
    // and visible to a crawler as a route into the catalogue.
    <div className={`${width} shrink-0 group card-hover`}>
      <Link
        to={to}
        aria-label={`${title} by ${book.author}`}
        className="block relative rounded-xl overflow-hidden cover-shadow aspect-[2/3] bg-secondary focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-400 focus-visible:ring-offset-2 focus-visible:ring-offset-background"
      >
        <BookCover book={book} showBrand={false} className="w-full h-full transition-transform duration-500 group-hover:scale-105" />
        <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />

        <div className="absolute top-2 left-2 right-2 flex items-start justify-between">
          {owned ? (
            <span className="flex items-center gap-1 px-2 py-0.5 rounded-full bg-emerald-500/90 text-emerald-950 text-[10px] font-bold backdrop-blur">
              <Check className="w-2.5 h-2.5" /> {t("owned")}
            </span>
          ) : (
            <span className="px-2 py-0.5 rounded-full bg-black/55 text-brand-300 text-[11px] font-bold backdrop-blur">
              £{Number(book.price).toFixed(2)}
            </span>
          )}
          <button
            onClick={handleWish}
            aria-label={wished ? t("in_wishlist") : t("add_wishlist")}
            className={`w-7 h-7 rounded-full flex items-center justify-center backdrop-blur transition-all opacity-0 group-hover:opacity-100 focus:opacity-100 focus-visible:ring-2 focus-visible:ring-brand-400 ${
              wished ? "bg-brand-500 text-brand-950 opacity-100" : "bg-black/50 text-white hover:bg-black/70"
            }`}
          >
            <Heart className="w-3.5 h-3.5" fill={wished ? "currentColor" : "none"} />
          </button>
        </div>

        <button
          onClick={handlePlay}
          aria-label={owned ? `${t("listen_now")}: ${title}` : `${t("play_sample")}: ${title}`}
          className="absolute bottom-3 right-3 w-11 h-11 rounded-full bg-brand-500 text-brand-950 flex items-center justify-center shadow-lg opacity-0 translate-y-2 group-hover:opacity-100 group-hover:translate-y-0 focus:opacity-100 focus:translate-y-0 transition-all duration-300 hover:bg-brand-400 hover:scale-105 focus-visible:ring-2 focus-visible:ring-brand-200"
        >
          <Play className="w-5 h-5 ml-0.5" fill="currentColor" />
        </button>
      </Link>

      <div className="mt-3 px-0.5">
        <h3 className="text-sm font-semibold leading-snug line-clamp-2">
          <Link to={to} className="hover:text-brand-400 transition-colors">{title}</Link>
        </h3>
        {/* Both names lead somewhere. On an audiobook the narrator is half the
            reason for the purchase, so it is a route, not a caption. */}
        <p className="text-xs text-muted-foreground mt-1 truncate">
          <Link
            to={`/author/${encodeURIComponent(book.author)}`}
            className="hover:text-brand-400 transition-colors"
          >
            {book.author}
          </Link>
        </p>
        {book.narrator && (
          <p className="text-xs text-muted-foreground/80 mt-0.5 truncate flex items-center gap-1">
            <Mic className="w-3 h-3 shrink-0" />
            <Link
              to={`/narrator/${encodeURIComponent(book.narrator)}`}
              className="hover:text-brand-400 transition-colors truncate"
            >
              {book.narrator}
            </Link>
          </p>
        )}
        <div className="flex items-center gap-3 mt-1.5">
          <span className="flex items-center gap-0.5 text-xs text-brand-400 font-medium">
            <Star className="w-3 h-3" fill="currentColor" /> {book.ratingCount > 0 ? Number(book.rating).toFixed(1) : "New"}
          </span>
          <span className="flex items-center gap-0.5 text-[11px] text-muted-foreground">
            <Clock className="w-3 h-3" /> {formatDuration(book.duration)}
          </span>
        </div>
      </div>
    </div>
  );
}
